import React, { createContext, useContext, useReducer } from "react";
import gql from "graphql-tag";
import { getCookie, getZeroRedemptionOfferValidationMessage } from "../../utility";
import moment from "moment";
import _ from "lodash";
import { OfferTypeCell } from "../../components";

const DateTimeFmt = "M/D/YY HH:mm";
export const tableViewModel = createContext();
tableViewModel.displayName = "Table Context";
export const useTableViewModelContext = () => {
  return useContext(tableViewModel);
};
const dateSortFn = (accessor, sortOrder) => (a, b) => {
  if (typeof accessor === "function") {
    return (moment(accessor(a.node), DateTimeFmt) - moment(accessor(b.node), DateTimeFmt)) * sortOrder;
  } else {
    return (moment(accessor[a.node]) - moment(accessor[b.node])) * sortOrder;
  }
};

export const columns = {
  member: [
    {
      header: "Name",
      accessor: (row) => `${row.firstName} ${row.lastName}`,
      link: (row) => `/admin/manage/member/${encodeURIComponent(row.userId)}`
    },
    { header: "Email", accessor: "email" },
    //{ header: "City", accessor: "city" },
    { header: "Status", accessor: "userStatus", filterExactMatch: true },
    { header: "Role", accessor: "userType" },
    {
      header: "Redemptions",
      accessor: (row) => row.redemptions && row.redemptions.length,
      link: (row) =>
        `/admin/table/redemption/Member/${encodeURIComponent(`${row.firstName} ${row.lastName}`)}`,
      style: { justifyContent: "center" }
    },
    { header: "Created", accessor: (row) => moment(row.created).format(DateTimeFmt), sortFn: dateSortFn },
    {
      header: "Last Update",
      accessor: (row) => moment(row.lastUpdate).format(DateTimeFmt),
      sortFn: dateSortFn
    }
  ],
  client: [
    {
      header: "Name",
      accessor: (row) => `${row.firstName} ${row.lastName}`,
      link: (row) => {
        return `/admin/manage/client/${encodeURIComponent(row.userId)}`;
      }
    },
    { header: "Email", accessor: "email" },
    {
      header: "Venue",
      accessor: (row) => row.venues.map((venue) => venue.label),
      link: (row) => row.venues.map((venue) => `/admin/manage/venue/${encodeURIComponent(venue.value)}`)
    },
    { header: "Status", accessor: "userStatus" },
    { header: "Created", accessor: (row) => moment(row.created).format(DateTimeFmt) }
  ],
  venue: [
    {
      header: "Name",
      accessor: "name",
      link: (row) => `/admin/manage/venue/${encodeURIComponent(row.venueId)}`
    },
    { header: "Area", accessor: "area" },
    { header: "Status", accessor: "status" },
    {
      header: "Clients",
      accessor: (row) => {
        return row.users.map((user) => `${user.firstName.substr(0, 1)} ${user.lastName}`);
      },
      link: (row) => row.users.map((user) => `/admin/manage/client/${encodeURIComponent(user.userId)}`),
      style: { justifyContent: "center" }
    },
    {
      header: "Offers",
      accessor: (row) => {
        return row.venueOffers && row.venueOffers.length;
      },
      link: (row) => `/admin/table/offer/Venue/${encodeURIComponent(row.name)}`,
      style: { justifyContent: "center" }
    },
    {
      header: "Redeemed",
      accessor: (row) => row.redemptions && row.redemptions.length,
      link: (row) => `/admin/table/redemption/Venue/${encodeURIComponent(row.name)}`,
      style: { justifyContent: "center" }
    },
    { header: "Created", accessor: (row) => moment(row.created).format(DateTimeFmt) }
  ],
  offer: [
    { header: "Venue", accessor: (row) => row.venues && row.venues[0] && row.venues[0].label },
    {
      header: "Offer Type",
      accessor: (row) => `${JSON.parse(row.offerType).label}`,
      link: (row) => `/admin/manage/offer/${encodeURIComponent(row.offerId)}`
    },
    { header: "Status", accessor: (row) => getZeroRedemptionOfferValidationMessage(row).message },
    {
      header: "Redemptions",
      accessor: (row) => row.redemptions && row.redemptions.length,
      link: (row) => `/admin/table/redemption/Offer/${encodeURIComponent(JSON.parse(row.offerType).label)}`
    },
    { header: "Published", accessor: (row) => moment(row.published).format(DateTimeFmt) }
  ],
  redemption: [
    {
      header: "Member",
      accessor: (row) => (row.user ? `${row.user.firstName} ${row.user.lastName}` : ""),
      link: (row) => `/admin/manage/member/${encodeURIComponent(row.userId)}`
    },
    { header: "Email", accessor: (row) => (row.user ? `${row.user.email}` : "") },
    {
      header: "Venue",
      accessor: (row) => (row.venue && row.venue.name) || "Deleted",
      link: (row) => `/admin/manage/venue/${encodeURIComponent(row.venueId)}`
    },
    {
      header: "Offer",
      accessor: (row) => (row.offer && JSON.parse(row.offer.offerType).label) || "Deleted",
      link: (row) => `/admin/manage/offer/${encodeURIComponent(row.offerId)}`
    },
    { header: "Date", accessor: "date" },
    { header: "Time", accessor: (row) => moment(row.time, "h:m:s").format("hh:mm A") }
  ],
  clientRedemption: [
    {
      header: "Venue",
      accessor: (row) => (row.venues && row.venues[0] && row.venues[0].label) || "Deleted",
      link: (row) =>
        row.venues && row.venues[0] && row.venues[0].value
          ? `/client/manage/venue/${encodeURIComponent(row.venues[0].value)}`
          : undefined
    },
    {
      header: "Offer Type",
      accessor: (row) => {
        let offer;
        if (row.offers?.length > 0) {
          offer = row.offers[0];
        }
        offer.venues = row.venues;
        offer.offerId = offer.Id;
        if (offer) {
          return <OfferTypeCell offer={offer} />;
        }
        return "Not Found / Deleted";
      }
    },
    { header: "Member", accessor: (row) => `${row.firstName} ${row.lastName}` },
    { header: "Email", accessor: "email" },
    { header: "Redemption Date", accessor: "date" },
    { header: "Redemption Time", accessor: (row) => moment(row.time, "h:m:s").format("hh:mm A") }
  ],
  accessCode: [
    { header: "Code", accessor: "code" },
    {
      header: "Applied Date",
      accessor: (row) => (row.appliedDate ? moment(row.appliedDate).format("YYYY-MM-DD") : "Not Applied")
    },
    {
      header: "Member",
      accessor: (row) => (row.user ? `${row.user.firstName} ${row.user.lastName}` : ""),
      link: (row) => `/admin/manage/member/${encodeURIComponent(row.userId)}`
    }
  ]
};

const memberQuery = gql`
  query {
    membersAndAdmins {
      edges {
        node {
          userId
          firstName
          lastName
          email
          city
          userStatus
          userType
          created
          lastUpdate
          redemptions {
            redemptionId
          }
        }
      }
    }
  }
`;

const clientQuery = gql`
  query {
    clients {
      edges {
        node {
          userId
          firstName
          lastName
          email
          city
          userStatus
          created
          venues {
            value
            label
          }
        }
      }
    }
  }
`;

const venueQuery = gql`
  query {
    venues {
      edges {
        node {
          venueId
          name
          status
          created
          area
          users {
            userId
            firstName
            lastName
          }
          venueOffers {
            venueOfferId
            offerId
          }
          redemptions {
            redemptionId
          }
        }
      }
    }
  }
`;

const offerQuery = gql`
  query {
    offers {
      edges {
        node {
          offerId
          offerType
          offerStatus
          published
          startDate
          endDate
          timeValidStart
          timeValidEnd
          redemptionLink
          validDays
          venues {
            value
            label
          }
          redemptions {
            redemptionId
          }
        }
      }
    }
  }
`;

const redemptionQuery = gql`
  query {
    allRedemptions {
      edges {
        node {
          redemptionId
          userId
          venueId
          offerId
          date
          time
          user {
            firstName
            lastName
            email
          }
          venue {
            name
          }
          offer {
            offerType
          }
        }
      }
    }
  }
`;

const clientRedemptionQuery = gql`
  query ($userCookie: String!) {
    clientRedemption(userCookie: $userCookie) {
      edges {
        node {
          redemptionId
          date
          time
          firstName
          lastName
          userType
          email
          venues {
            value
            label
          }
          offers {
            Id
            offerId
            header
            description
            promo
            disclaimer
            image
            startDate
            endDate
            maxValue
            offerType
            offerStatus
            published
            usesPerDay
          }
        }
      }
    }
  }
`;

const accessCodeQuery = gql`
  query {
    allAccessCodes {
      edges {
        node {
          accessCodeId
          userId
          expiredDate
          appliedDate
          code
          user {
            firstName
            lastName
          }
        }
      }
    }
  }
`;

const defaultState = {
  member: {
    query: memberQuery,
    accessor: "membersAndAdmins",
    identifier: "userId"
  },
  client: {
    query: clientQuery,
    accessor: "clients",
    identifier: "userId"
  },
  venue: {
    query: venueQuery,
    accessor: "venues",
    identifier: "venueId",
    title: "Course Table"
  },
  offer: {
    query: offerQuery,
    accessor: "offers",
    identifier: "offerId"
  },
  redemption: {
    query: redemptionQuery,
    accessor: "allRedemptions",
    identifier: "redemptionId"
  },
  clientRedemption: {
    query: clientRedemptionQuery,
    variables: { userCookie: getCookie("uuid") },
    accessor: "clientRedemption",
    identifier: "redemptionId",
    title: "Redemption Table"
  },
  accessCode: {
    query: accessCodeQuery,
    accessor: "allAccessCodes",
    identifier: "accessCodeId"
  }
};

// tables const with all table names / attributes
const TABLES = {};

Object.keys(defaultState).forEach((key) => {
  defaultState[key].columns = columns[key];
  defaultState[key].sortBy = undefined;
  defaultState[key].sortOrder = 1; // 1 for asc, -1 for desc
  defaultState[key].rows = [];
  defaultState[key].filtered = [];
  defaultState[key].isFilterVisible = false;
  defaultState[key].isLoading = false;
});

const DispatchFn = (state, action) => {
  switch (action.type) {
    case "SET_LOADING": {
      const { table, isLoading } = action.payload;
      const newState = { ...state };
      newState[table].isLoading = isLoading;
      return newState;
    }
    case "SORT_BY": {
      const { table, accessor } = action.payload;
      const newState = { ...state };
      if (newState[table].sortBy === accessor) {
        newState[table].sortOrder *= -1;
      } else {
        newState[table].sortOrder = 1; // asc
      }
      newState[table].sortBy = accessor;

      let columnSortFn = false;
      for (let i = 0; i < columns[table].length; i++) {
        if (columns[table][i].accessor === accessor) {
          columnSortFn = columns[table][i].sortFn;
          break;
        }
      }

      let sortFn;
      if (columnSortFn) {
        sortFn = columnSortFn(accessor, newState[table].sortOrder);
      } else {
        sortFn = (a, b) => {
          if (typeof accessor === "function") {
            return accessor(a.node) > accessor(b.node)
              ? 1 * newState[table].sortOrder
              : -1 * newState[table].sortOrder;
          } else {
            return a.node[accessor] > b.node[accessor]
              ? 1 * newState[table].sortOrder
              : -1 * newState[table].sortOrder;
          }
        };
      }

      // do the sorting here
      const sortedData = Array.from(newState[table].rows.sort(sortFn));
      const sortedFilter = Array.from(newState[table].filtered.sort(sortFn));
      newState[table].rows = sortedData;
      newState[table].filtered = sortedFilter;
      // newState[table].isLoading = false;
      return newState;
    }
    case "SET_TABLE_ROWS": {
      const { table, rows } = action.payload;
      const newState = { ...state };
      newState[table].rows = [...rows];
      newState[table].filtered = [...rows];
      return newState;
    }
    case "SET_IDS_COLUMN": {
      const { table, ids, column, value } = action.payload;
      const newState = { ...state };
      newState[table].rows.forEach((row, idx) => {
        if (ids.includes(row.node[newState[table].identifier])) {
          row.node[column] = value;
        }
      });
      return newState;
    }
    case "DELETE_IDS": {
      const { table, ids } = action.payload;
      const newState = { ...state };
      newState[table].rows = newState[table].rows.filter(
        (row) => !ids.includes(row.node[newState[table].identifier])
      );
      newState[table].filtered = newState[table].filtered.filter(
        (row) => !ids.includes(row.node[newState[table].identifier])
      );
      return newState;
    }
    case "FILTER_DATA": {
      const { table, filters } = action.payload;
      const newState = { ...state };

      let filtered = [...newState[table].rows];
      filtered = filtered.filter((row) => {
        let showRow = true;
        columns[table].forEach((col) => {
          let value;
          if (typeof col.accessor === "function") {
            value = col.accessor(row.node);
          } else {
            value = row.node[col.accessor];
          }
          const mustBeExactMatch = !!col.filterExactMatch;
          const searchVal = new RegExp(filters[col.header], "i");
          if (
            filters[col.header]?.length > 0 &&
            (value || value === 0 || value === "") &&
            ((mustBeExactMatch && value.toString() !== filters[col.header]) ||
              (!mustBeExactMatch && value.toString().search(searchVal) === -1))
          ) {
            showRow = false;
          }
        });
        return showRow;
      });
      newState[table].filtered = filtered;
      return newState;
    }
    case "RESET_DATA": {
      const table = action.payload;
      const newState = { ...state };
      newState[table].filtered = [...newState[table].rows];
      return newState;
    }
    case "CLEAR_REDEMPTIONS": {
      const { table, ids } = action.payload;
      const newState = _.cloneDeep(state);
      newState[table].rows.forEach((row, idx) => {
        if (ids.includes(row.node[newState[table].identifier])) {
          newState[table].rows[idx].redemptions = [];
        }
      });
      newState[table].filtered.forEach((row, idx) => {
        if (ids.includes(row.node[newState[table].identifier])) {
          newState[table].filtered[idx].redemptions = [];
        }
      });
      return newState;
    }
    default:
      return { ...state };
  }
};

export const TableProvider = ({ children, defaultData }) => {
  const [state, dispatch] = useReducer(DispatchFn, Object.assign({}, defaultState, defaultData));

  return <tableViewModel.Provider value={[state, dispatch]}>{children}</tableViewModel.Provider>;
};
